import {setLocale as setVeeLocale} from "@vee-validate/i18n"
import {defineStore} from 'pinia'
import type {SeasonResource} from "~/types/resources/season"

type AuthDataResponse = {
    abilities: Array<{ subject: string, action: string }>,
    impersonate: boolean,
    userData: {
        id: number,
        role_name: string,
        firstname: string,
        lastname: string,
        fullname: string,
        locale: 'en' | 'de',
        organisation_name: string | null,
        organisation_sport_id: number | null,
        organisation_id: number,
        organisation_token: string | null,
        organisations: Array<{ id: number; name: string, sport_name: string }>,
        sport_use_invoice: boolean,
        season_id: number | null,
        season_name: string | null,
        season_sport_id: number | null,
        season_start_date: string | null,
        season_end_date: string | null,
        season: SeasonResource | null,
        email: string,
        birthday: string | null,
        image_url: string | null,
        verified: boolean,
        active: boolean,
    },
    lastSelectedGameClassId: number | null
}

export const useAuthStore = defineStore('auth', () => {
    // convert to a function
    const isAuthenticated = ref<boolean | null>(null)
    const abilities = ref<AuthDataResponse['abilities']>([])
    const userData = ref<AuthDataResponse['userData'] | null>({
        id: 0,
        role_name: '',
        firstname: '',
        lastname: '',
        fullname: '',
        locale: 'de',
        organisation_name: null,
        organisation_sport_id: null,
        organisation_id: 0,
        organisation_token: '',
        organisations: [],
        sport_use_invoice: true,
        season_id: null,
        season_name: null,
        season_sport_id: null,
        season_start_date: null,
        season_end_date: null,
        season: null,
        email: '',
        birthday: null,
        image_url: null,
        verified: false,
        active: false,
    })
    const lastSelectedGameClassId = ref<number | null>(null)
    const shake = ref(false)
    const remoteVersion = ref<string | null>(null)
    const impersonate = ref(false)
    const redirectPathAfterLogin = ref<string | null>(null)

    //https://pinia.vuejs.org/core-concepts/actions.html#subscribing-to-actions
    async function successfulLogin(redirectPath = '/service/dashboard', query = {}) {
        return await new Promise((resolve, reject) => {
            try {
                isAuthenticated.value = true
                getAuthData().then(() => {
                    if (redirectPathAfterLogin.value) {
                        redirectPath = redirectPathAfterLogin.value
                        redirectPathAfterLogin.value = null
                    }
                    navigateTo({
                        path: redirectPath,
                        query: query
                    }, {external: true})
                    resolve(true)
                })
            } catch (error) {
                reject(error)
            }
        })
    }

    async function getAuthData() {
        const response = await $lara.get<AuthDataResponse>(useNuxtApp().$apiRoute('spa.authData'))
        if (response.error) {
            return
        }
        setAuthData(response)
    }

    function setAuthData(data: AuthDataResponse) {
        isAuthenticated.value = true
        abilities.value = data.abilities
        userData.value = data.userData
        impersonate.value = data.impersonate

        if (userData.value?.locale) {
            const lang = useLocalStorage<'de' | 'en'>('lang', userData.value?.locale)
            lang.value = useFeature('multiLanguage') ? userData.value.locale : 'de'
            useNuxtApp().$i18n.setLocale(lang.value)
            setVeeLocale(lang.value)
            $lara.put(useNuxtApp().$apiRoute('spa.settings.locale'), {body: {locale: lang.value}})
        }

        localStorage.setItem('isAuthenticated', 'true')
        localStorage.setItem('abilities', JSON.stringify(data.abilities))
        localStorage.setItem('userData', JSON.stringify(data.userData))
    }

    function setRemoteVersion(version: string) {
        remoteVersion.value = version
    }

    function resetAuthData() {
        isAuthenticated.value = false
        abilities.value = []
        userData.value = null
        impersonate.value = false

        localStorage.setItem('isAuthenticated', 'null')
        localStorage.setItem('abilities', 'null')
        localStorage.setItem('userData', 'null')
    }

    async function logout(redirectPath = '/') {
        await $lara.post('/app/spa/logout')
        resetAuthData()
        navigateTo({
            path: redirectPath
        })
    }

    return {
        isAuthenticated,
        abilities,
        userData,
        lastSelectedGameClassId,
        remoteVersion,
        impersonate,
        redirectPathAfterLogin,
        shake,
        successfulLogin,
        getAuthData,
        setRemoteVersion,
        resetAuthData,
        logout
    }
})
